import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { pendingResetFromGrant } from '@turn-connect/shared';
import './App.css';
import { useAuth } from './hooks/useAuth';
import { useThemeSync } from './theme/ThemeProvider';
import { useTermsAcceptance } from './hooks/useTermsAcceptance';
import { User, adminService } from './services/auth';
import { AccountProvider } from './hooks/useAccountContext';
import { AccountSwitchProvider, performAccountSwitch, type AccountSwitchDeps } from './hooks/useAccountSwitch';
import ErrorBoundary from './components/ErrorBoundary';
import { usePermissions } from '@turn-connect/hooks';
import { GlobalHeader, MainNavigation } from './components/navigation';
import { useAccountSelection } from './hooks/useAccountSelection';
import { useLocationSelection } from './hooks/useLocationSelection';
import { useSessionLocationSync, type LocationSwitchOutcome } from './hooks/useSessionLocationSync';
import { SelectOption, useOptionalToast } from '@turn-connect/ui';
import { logCookieDiagnostics, cleanupDuplicateCookies, getCookieRecommendations, getTotalCookieSize } from './utils/cookieManager';
import { setupFetchInterceptor, teardownFetchInterceptor } from './utils/fetchInterceptor';
import TermsAcceptanceModal from './components/modals/TermsAcceptanceModal';
import UserSwitchModal from './components/modals/UserSwitchModal';
import SetNewPasswordModal from './components/auth/SetNewPasswordModal';
import { usePendingPasswordReset, shouldOfferPasswordReset } from './hooks/usePendingPasswordReset';
import { useQueryClient } from '@tanstack/react-query';
import { useAccountLocations, useMultiAccountOptions, useMyLocations, useSystemAdminAccounts } from './queries/appData';
import { refreshLocationListsAfterSwitch } from './queries/sessionScope';
import { clearRoleLocks, useRoleLockViewerSync } from './queries/roleLockScope';
import { lazyRetry } from './utils/lazyRetry';
import { announceUnavailableAccount, isUnavailableAccountError, pickFallbackAccount } from './utils/staleAccount';
import { MyWorkProvider } from './hooks/useMyWork';
import { canShowExecutiveDashboard, getDashboardGates } from './utils/dashboardAccess';
import { navigateToLocationDashboard, clearCreateParams, clearPlaceParams } from './utils/navigation';
import { reportParamsFromUrl, writeReportAddress } from './utils/reportLinks';
import {
  clearPostLoginRedirect,
  dashboardSearch,
  isResourceDeepLink,
  recordLinkKind,
  recordLinkOnly,
  savePostLoginRedirect,
  takePostLoginRedirect,
} from './utils/postLoginRedirect';
import { SignOutProvider } from './hooks/useSignOut';
import { helpGuideSearch } from './utils/guideLinks';
import { parseLocationSettingsLink, stripLocationSettingsLink, type LocationSettingsLink } from './utils/locationSettingsLink';
import { SETTINGS_PAGE_HOST_CSS, SETTINGS_PAGE_HOST_ID } from './components/settings/page/SettingsPageFrame';
import {
  createAccountSettingsLinkFollower,
  followAccountSettingsLinkOnce,
  resetAccountSettingsLinkFollower,
  type AccountSettingsLinkFollower,
  parseAccountSettingsLink,
  stripAccountSettingsLink,
  type AccountSettingsLink,
} from './utils/accountSettingsLink';
import {
  OPEN_SETTINGS_EVENT,
  isAnySettingsDialogOpen,
  isSettingsDialogHeld,
  nextShellSettingsLink,
  openSettings,
  leaveSettingsThen,
  type SettingsTarget,
} from './utils/settingsDialogs';
import { leaveSettingsAndGo, whenSettingsHistoryIdle } from './hooks/useSettingsDialogUrl';
import { quickCreateLocationId } from './utils/quickCreateLocation';
import { announceWorkOrderCreated } from './utils/workOrderEvents';
import QuickKeys from './components/palette/QuickKeys';
import type { SearchSelection } from './components/navigation/GlobalHeader';
import { isDeveloperSite } from './components/developer/site';
import { registerWorkOrderReturns } from './workOrderReturns';
import { navigateToCustomers } from './features/customers/customersNavigation';
import { useSignInLanding } from './features/navigation/useLanding';

// Where a work order goes back to when it closes (a customer's page, …).
registerWorkOrderReturns();

// Lazy load heavy components
const Dashboard = lazyRetry(() => import('./components/dashboards/LocationDashboard'));
const UserProfile = lazyRetry(() => import('./components/UserProfile'));
const AdminDashboard = lazyRetry(() => import('./components/AdminDashboard'));
const WorkOrders = lazyRetry(() => import('./components/WorkOrders'));
const Reports = lazyRetry(() => import('./components/Reports'));
const MyWorkPage = lazyRetry(() => import('./components/mywork/MyWorkContainer'));
const LoginScreen = lazyRetry(() => import('./components/LoginScreen'));
const ExecutiveDashboard = lazyRetry(() => import('./components/dashboards/executive/ExecutiveDashboard'));
const LocationNoAccess = lazyRetry(() => import('./components/dashboards/location/LocationNoAccess'));
// Opens a location's settings tab over the location dashboard when the URL
// names one (the geofence notification's ?tab=time-attendance&subtab=geofence).
const DeepLinkedLocationModal = lazyRetry(() => import('./components/modals/DeepLinkedLocationModal'));
// Opens an account settings tab when the URL names one (the partner service
// codes notification's ?account_tab=partners&partner_connection=…).
const DeepLinkedAccountModal = lazyRetry(() => import('./components/modals/DeepLinkedAccountModal'));
const Inventory = lazyRetry(() => import('./components/inventory/Inventory'));
const HelpCenter = lazyRetry(() => import('./components/help/HelpCenter'));
// The optional Customers module's view (shown only while the account has it on).
const CustomersView = lazyRetry(() => import('./features/customers/CustomersView'));
const PartnerDocs = lazyRetry(() => import('./components/public/PartnerDocs'));
// The partner developer site (developer.* hosts) — one lazy chunk, see AppRoot.
const DeveloperSite = lazyRetry(() => import('./components/developer/DeveloperSite'));
const SupportTicketPage = lazyRetry(() => import('./components/public/SupportTicketPage'));
const SmsTermsPage = lazyRetry(() => import('./components/SmsTermsPage'));
// Create-work-order modal, mounted at App level so "New Work Order" (nav
// button, palette, `n` key) opens IN PLACE over whatever view is showing —
// no bounce through the work-order list view. Lazy: it's a large module and
// only loads the first time the user actually creates.
const WorkOrderCreateModal = lazyRetry(() => import('./components/modals/WorkOrderModal'));

// Public pages
const HomePage = lazyRetry(() => import('./components/public/HomePage'));
const DemoPage = lazyRetry(() => import('./components/public/DemoPage'));
const SharedResourcePage = lazyRetry(() => import('./components/public/SharedResourcePage'));
const SolutionsPage = lazyRetry(() => import('./components/public/SolutionsPage'));
const IntegrationsPage = lazyRetry(() => import('./components/public/IntegrationsPage'));
const PrivacyPage = lazyRetry(() => import('./components/public/PrivacyPage'));
const SupportPage = lazyRetry(() => import('./components/public/SupportPage'));
const InvitationAcceptPage = lazyRetry(() => import('./components/public/InvitationAcceptPage'));

// Customer-segment + integration landing deep-links: render the Solutions /
// Integrations overview with per-route SEO heads (see publicRoutes.ts).
const DealerGroupsPage = lazyRetry(() => import('./components/public/solutions/DealerGroupsPage'));
const MobileRepairPage = lazyRetry(() => import('./components/public/solutions/MobileRepairPage'));
const FleetServicePage = lazyRetry(() => import('./components/public/solutions/FleetServicePage'));
const QuickServicePage = lazyRetry(() => import('./components/public/solutions/QuickServicePage'));
const IndependentShopsPage = lazyRetry(() => import('./components/public/solutions/IndependentShopsPage'));
const NetSuiteIntegrationPage = lazyRetry(() => import('./components/public/integrations/NetSuiteIntegrationPage'));
const QuickBooksIntegrationPage = lazyRetry(() => import('./components/public/integrations/QuickBooksIntegrationPage'));

type AppView =
  | 'dashboard'
  | 'dashboard:accounts'
  | 'dashboard:location'
  | 'workorders'
  | 'customers'
  | 'reports'
  | 'mywork'
  | 'profile'
  | 'admin'
  | 'inventory'
  | 'help'
  | 'supportTicket';

// Clean-URL routing for public pages: read window.location.pathname
// at boot and map it to a known route. Authenticated app routing
// continues to use ?view=X; this only touches the public surface.
//
// nginx already serves /index.html for unknown paths under the SPA base
// via `try_files $uri $uri/ /index.html`, so paths like
// /solutions/dealer-groups land on this SPA and we route them here.
//
// We DON'T rewrite the URL to a query-string version — the address bar
// stays clean, so copy/paste shares produce the canonical URL.
//
// The app is served at base "/", so PUBLIC_URL is empty and the
// base-strip below is a no-op. Old /web/* links are 301-redirected to
// /* by nginx before they ever reach this resolver.
function resolvePathnameToPage(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  // Note: bare "/" is intentionally NOT mapped here. Adding "/" → "home"
  // would make publicPage === 'home' for everyone, which the public-page
  // block renders BEFORE the authenticated branch — so authenticated users
  // at / would see the marketing HomePage instead of their dashboard.
  // Instead, the home-vs-login decision for the bare root is made in the
  // render flow: authenticated users get the dashboard, and unauthenticated
  // users fall through to the marketing HomePage (only explicit attempts to
  // reach authenticated content — ?view=, ?wo=, ?noteId= — go to login).
  // The ?page=home query still explicitly requests the marketing surface.
  // Clean URLs added in Phase 2 are additive: /demo, /solutions/*,
  // /integrations/* etc. resolve here.
  const PATH_TO_PAGE: Record<string, string> = {
    '/demo': 'demo',
    '/support': 'support',
    '/privacy': 'privacy',
    '/sms-terms': 'sms-terms',
    '/login': 'login',
    '/invitation-accept': 'invitation-accept',
    '/solutions': 'solutions',
    '/integrations': 'integrations',
    '/solutions/dealer-groups': 'solutions/dealer-groups',
    '/solutions/mobile-repair': 'solutions/mobile-repair',
    '/solutions/fleet-service': 'solutions/fleet-service',
    // Retired segment. The URL is indexed and linked, so it still
    // resolves — to the Solutions overview, since the segment it named
    // no longer exists and pointing it at an unrelated one would
    // mislead. Deliberately absent from publicRoutes.ts, so it is no
    // longer advertised in the sitemap or given an OG card — the same
    // treatment the retired /guide/<slug> URLs get.
    '/solutions/collision-centers': 'solutions',
    '/solutions/quick-service': 'solutions/quick-service',
    '/solutions/independent-shops': 'solutions/independent-shops',
    '/integrations/netsuite': 'integrations/netsuite',
    '/integrations/quickbooks': 'integrations/quickbooks',
  };
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  let pathname = window.location.pathname;
  if (base && pathname.startsWith(base)) pathname = pathname.slice(base.length) || '/';
  if (pathname.length > 1 && pathname.endsWith('/')) pathname = pathname.slice(0, -1);
  // Clean guide URLs: /guide/<slug> → page "guide". The slug is read back
  // from the path by resolveGuideIdFromPath() when rendering the page.
  if (pathname.startsWith('/guide/')) return 'guide';
  // Partner developer docs: /partners/docs → the public API reference + guides
  // for partners integrating with the platform.
  if (pathname.startsWith('/partners/docs')) return 'partner-docs';
  // Public share links: /share/<token> → read-only shared work order / photo.
  if (pathname.startsWith('/share/')) return 'share';
  // Support portal: /support/t/<token> → the customer's own ticket thread.
  // Deliberately public — the link is emailed, and the reader may be locked out
  // of the app or not be a Turn Connect user at all.
  if (pathname.startsWith('/support/t/')) return 'supportTicket';
  return PATH_TO_PAGE[pathname];
}

// Pathname to use when constructing AUTHENTICATED app URLs. Clean public
// paths (e.g. /login since the nav moved off ?page= links) must not be
// retained: the public-page resolver would route the very next render back
// to the public page, looping the post-login redirect into a blank skeleton.
function authedPathname(): string {
  if (typeof window === 'undefined') return '/';
  if (!resolvePathnameToPage()) return window.location.pathname;
  return (process.env.PUBLIC_URL || '').replace(/\/+$/, '') || '/';
}

// Derive the portal token from a clean /support/t/<token> URL.
function resolveSupportTokenFromPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  let pathname = window.location.pathname;
  if (base && pathname.startsWith(base)) pathname = pathname.slice(base.length) || '/';
  if (pathname.length > 1 && pathname.endsWith('/')) pathname = pathname.slice(0, -1);
  const match = pathname.match(/^\/support\/t\/(.+)$/);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// Derive the share token from a clean /share/<token> URL.
function resolveShareTokenFromPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  let pathname = window.location.pathname;
  if (base && pathname.startsWith(base)) pathname = pathname.slice(base.length) || '/';
  if (pathname.length > 1 && pathname.endsWith('/')) pathname = pathname.slice(0, -1);
  const match = pathname.match(/^\/share\/(.+)$/);
  if (!match) return undefined;
  // Tolerate percent-encoded tokens (e.g. a trailing '=' pasted as %3D).
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// Derive the guide slug from a clean /guide/<slug> URL. Returns undefined
// for non-guide paths so the caller can fall back to the ?id= query param.
function resolveGuideIdFromPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  let pathname = window.location.pathname;
  if (base && pathname.startsWith(base)) pathname = pathname.slice(base.length) || '/';
  if (pathname.length > 1 && pathname.endsWith('/')) pathname = pathname.slice(0, -1);
  const match = pathname.match(/^\/guide\/(.+)$/);
  return match ? match[1] : undefined;
}

function App() {
  const [currentView, setCurrentView] = useState<AppView>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlView = params.get('view') as AppView | null;
      if (urlView) return urlView;
      const stored = localStorage.getItem('current_view');
      return stored ? (JSON.parse(stored) as AppView) : 'dashboard';
    } catch {
      return 'dashboard';
    }
  });
  const [navigationParams, setNavigationParams] = useState<{
    account?: string;
    location?: string;
    department?: string;
    /** Deep link into a specific report and, where it has one, a sub-tab. */
    report?: string;
    reportView?: string;
    /** A section of the target screen to open (My Work: 'support'). */
    section?: string;
    /** Bumped per navigation so a repeat deep link re-applies downstream. */
    navSeq?: number;
  }>({});
  // A location settings tab the URL asks to open over the dashboard (tab= / subtab=).
  const [locationSettingsLink, setLocationSettingsLink] = useState<LocationSettingsLink | null>(null);
  // An account settings tab the URL asks to open (account_tab= / partner_connection=).
  const [accountSettingsLink, setAccountSettingsLink] = useState<AccountSettingsLink | null>(null);

  // Snapshot user data from localStorage before useAuth processes it.
  // This lets us detect if a magic link login switched to a different user.
  const [preAuthUser] = useState<{ id?: string; email?: string; first_name?: string; last_name?: string } | null>(() => {
    try {
      const stored = localStorage.getItem('user_data');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.id ? { id: parsed.id, email: parsed.email, first_name: parsed.first_name, last_name: parsed.last_name } : null;
      }
    } catch {}
    return null;
  });

  const { user, permissions, isLoading, isAuthenticated, error, login, loginFromPasswordless, logout, updateProfile, updatePermissions } =
    useAuth();
  // No permission refresh keyed on the selected location: every location
  // change is a real switch whose response carries that location's
  // permissions (overrides included). A /auth/me refresh raced it — sent
  // under the old cookie, it could land after the switch and restore the
  // previous location's permissions.
  // Apply the theme the server holds for this user at login (source of truth),
  // which also caches it locally for the next boot.
  useThemeSync(user?.theme);
  const { isSystemAdmin, isAccountAdmin } = usePermissions(user, permissions, 'system');

  // Check if user has an account-wide role assignment (for dashboard routing and location loading)
  // Account-wide roles are stored in user.accounts[].roles, not user.locations[].roles
  const userAccounts = (user as any)?.accounts || [];
  const hasAccountScopeRole = userAccounts.some((acc: any) => (acc?.roles || []).some((r: any) => r.is_active !== false));

  // Users who can access account-level features (load locations via API, see account dashboard)
  const canAccessAccountFeatures = isSystemAdmin || isAccountAdmin || hasAccountScopeRole;

  // Dashboard visibility gates — single source of truth in getDashboardGates
  // (mirrors backend RequirePermission; only system admins bypass). Resolved from
  // the current location-scoped `permissions`, so they adapt on location switch.
  const { canViewLocationDashboard } = getDashboardGates(user, permissions);
  // Executive dashboard visibility mirrors the backend (RequirePermission on
  // the permission alone): dashboards:executive:view can arrive via a per-user
  // override, which creates no account-scope role — requiring account context
  // here hid a dashboard the backend serves. The account is JWT-resolved
  // server-side, so no client-side account selection is needed.
  const showExecutiveDashboard = canShowExecutiveDashboard(user, permissions);

  // Landing guard: a user with no dashboard access must not dead-end on a
  // dashboard "no access" screen. Route them to the first screen they can use —
  // work orders if they can read them, otherwise reports (always available).
  // replaceState avoids adding a history entry. Admins always see a dashboard,
  // so they're never redirected here.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onDashboardView = currentView === 'dashboard' || currentView === 'dashboard:accounts' || currentView === 'dashboard:location';
    if (!onDashboardView) return;
    if (showExecutiveDashboard || canViewLocationDashboard) return;
    const fallback: AppView = permissions.includes('workorders:read') ? 'workorders' : 'reports';
    setCurrentView(fallback);
    try {
      localStorage.setItem('current_view', JSON.stringify(fallback));
    } catch {}
    const params = new URLSearchParams(window.location.search);
    params.set('view', fallback);
    try {
      window.history.replaceState({ view: fallback }, '', `?${params.toString()}`);
    } catch {}
  }, [isAuthenticated, currentView, showExecutiveDashboard, canViewLocationDashboard, permissions]);

  // Terms acceptance state - pending terms now come from login response
  const { hasPendingTerms, pendingTerms, setPendingTermsFromLogin, clearTermsState } = useTermsAcceptance();

  // Account and location selection state for MainNavigation
  const { accountId: selectedAccountId, select: selectAccount } = useAccountSelection();
  const { locationId: selectedLocationId, select: selectLocation } = useLocationSelection();
  // Track admin API loading errors to provide user feedback instead of silent failures
  const [adminLoadError, setAdminLoadError] = useState<string | null>(null);
  // True while switchAccount is re-scoping the JWT cookie server-side; gates
  // the locations query so it doesn't fetch the new account under the old scope.
  const [accountSwitchInFlight, setAccountSwitchInFlight] = useState(false);
  // Mirrored in a ref so the switch can read it back without waiting for a
  // re-render — two picks can land in the same tick.
  const accountSwitchInFlightRef = useRef(false);
  const setAccountSwitchInFlightBoth = useCallback((v: boolean) => {
    accountSwitchInFlightRef.current = v;
    setAccountSwitchInFlight(v);
  }, []);

  // App-level create-work-order modal (nav "New Work Order" button, command
  // palette, `n` key). Opens over the CURRENT view — the old flow navigated
  // to the work-order list first and bounced back to the dashboard after.
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);

  const queryClient = useQueryClient();
  // Role locks are the signed-in person's answer: keyed by them, dropped when they change.
  useRoleLockViewerSync(queryClient, user?.id);
  // Permissions as of the latest render, for comparing across a session switch.
  const permissionsRef = useRef(permissions);
  permissionsRef.current = permissions;
  // Every session re-scope reports the location it landed on, so the header
  // can be kept on the session's location (useSessionLocationSync, below).
  const recordSessionLocationRef = useRef<(locationId: string | null | undefined) => void>(() => {});
  const updateSessionPermissions = useCallback(
    (nextPermissions: string[], locationId?: string) => {
      recordSessionLocationRef.current(locationId);
      updatePermissions(nextPermissions, locationId);
    },
    [updatePermissions]
  );
  // Dependencies for the shared account switch (Reports, Executive and Departments switchers).
  const accountSwitchDeps: AccountSwitchDeps = useMemo(
    () => ({
      isSystemAdmin,
      selectAccount,
      selectLocation,
      updatePermissions: updateSessionPermissions,
      setSwitchInFlight: setAccountSwitchInFlightBoth,
      isSwitchInFlight: () => accountSwitchInFlightRef.current,
      queryClient,
    }),
    [isSystemAdmin, selectAccount, selectLocation, updateSessionPermissions, setAccountSwitchInFlightBoth, queryClient]
  );

  // --- Selector reference data (React Query; queryFns live in queries/appData.ts) ---
  // System admins: every account's id and name.
  const sysAdminAccountsEnabled = isSystemAdmin && isAuthenticated && !!user;
  const { accounts: sysAdminAccounts, error: sysAdminAccountsError } = useSystemAdminAccounts(sysAdminAccountsEnabled);

  // Non-admin users: accounts from /auth/accounts, JWT, or location-derived names.
  const multiAccountEnabled = isAuthenticated && !!user && !isSystemAdmin;
  const { accounts: multiAccountOptions, initialAccountId: multiAccountInitialId } = useMultiAccountOptions(user, multiAccountEnabled);

  // Locations for users with account-level access, keyed by the selected account.
  // Accounts this session has found unavailable (404/403 for their
  // locations — see utils/staleAccount). Every default-selection path skips
  // them, so a stale id that is also the user's own account_id cannot be
  // picked straight back.
  const [unavailableAccounts, setUnavailableAccounts] = useState<ReadonlySet<string>>(() => new Set());
  const usableAccount = useCallback(
    (id: string | null | undefined): string | null => (id && !unavailableAccounts.has(id) ? id : null),
    [unavailableAccounts]
  );
  const effectiveAccountId = selectedAccountId || usableAccount((user as any)?.account_id);
  // A non-admin's Executive dashboard is their session's account: a new account
  // starts it afresh (no group, page or search carried over from the last one).
  // Bumped by the logo: the executive dashboard starts again at its top.
  const [execResetCount, setExecResetCount] = useState(0);
  const executiveKey = `${isSystemAdmin ? 'executive' : `executive-${effectiveAccountId ?? ''}`}-${execResetCount}`;
  // The account the header's Settings and ⌘K settings results open: the one
  // in scope, never the "all accounts" pseudo-selection.
  const settingsScopeAccountId = effectiveAccountId && effectiveAccountId !== 'ALL' ? effectiveAccountId : null;
  const accountLocationsEnabled = isAuthenticated && !!user && canAccessAccountFeatures && !accountSwitchInFlight;
  const accountLocationsQuery = useAccountLocations(effectiveAccountId, accountLocationsEnabled);

  // A fresh sign-in lands by the shared landing rules: a shop with the
  // Customers module opens on its customers once its store is known. Begun by
  // the initial-view effect below when the address names no screen.
  const landingGates = useMemo(
    () => ({ canSeeExecutive: showExecutiveDashboard, canViewLocationDashboard, permissions }),
    [showExecutiveDashboard, canViewLocationDashboard, permissions]
  );
  const signInLanding = useSignInLanding({
    gates: landingGates,
    accountId: effectiveAccountId,
    locationId: selectedLocationId,
    // Account-level people get a store picked once the account's list arrives.
    locationSettled: !(isAuthenticated && !!user && canAccessAccountFeatures) || (!accountSwitchInFlight && !!accountLocationsQuery.data),
    currentView,
  });

  // Locations for users with only location-level access (/auth/my-locations).
  const myLocationsEnabled = isAuthenticated && !!user && !canAccessAccountFeatures;
  const myLocationsQuery = useMyLocations(user, myLocationsEnabled);
  const myLocationsData = myLocationsQuery.data;

  // Selector lists derived from query data — names kept for the JSX below.
  const accounts: SelectOption[] = (isSystemAdmin ? sysAdminAccounts : multiAccountOptions) ?? [];
  const locations: SelectOption[] = useMemo(() => {
    if (canAccessAccountFeatures) return accountLocationsQuery.data?.options ?? [];
    if (!myLocationsData) return [];
    // The synthesized fallback option has no accountId — never filter it out.
    if (myLocationsData.isFallback) return myLocationsData.options;
    // Filter locations by selected account for multi-account users
    return selectedAccountId ? myLocationsData.options.filter((loc) => loc.accountId === selectedAccountId) : myLocationsData.options;
  }, [canAccessAccountFeatures, accountLocationsQuery.data, myLocationsData, selectedAccountId]);
  // The locations in scope, without duplicates (two sources can list the same one).
  const uniqueLocations = useMemo(() => {
    const seen = new Set<string>();
    return locations.filter((loc) => {
      if (seen.has(loc.value)) return false;
      seen.add(loc.value);
      return true;
    });
  }, [locations]);

  // User switch prompt state: shown when a magic link authenticates a different user
  const [userSwitchPending, setUserSwitchPending] = useState<{ oldUser: any; newUser: any } | null>(null);

  // "Choose a new password" after a Forgot password? sign-in, or when the
  // user's manager requires one. Comes from the OTP verify response, or from
  // the URL fragment the API adds after an emailed reset link.
  const [pendingPasswordReset, setPendingPasswordReset] = usePendingPasswordReset();

  // True once this page has signed someone in (password or code). Until then,
  // a session found on load means the visitor was already signed in, and a
  // record link they opened must not be kept for a later sign-in.
  const signedInOnThisPageRef = useRef(false);

  // Save deep link params on mount (before auth check completes) as a safety net.
  // If the URL names a record (wo, noteId, batch), preserve it in sessionStorage
  // so they survive the auth flow redirect.
  useEffect(() => {
    savePostLoginRedirect(window.location.search);
  }, []); // Only on mount

  // Auto-redirect to login when authentication is lost
  useEffect(() => {
    if (!isLoading && !isAuthenticated && error) {
      // Clear any existing state and force login view
      setCurrentView('dashboard');
      // The login screen will be shown automatically when !isAuthenticated
    }
  }, [isLoading, isAuthenticated, error]);

  // Browser history support with query param `view`
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      const params = new URLSearchParams(window.location.search);
      const v = (params.get('view') as AppView | null) || undefined;
      if (v) setCurrentView(v);
      else setCurrentView('dashboard');

      // Update navigation params
      setNavigationParams((prev) => ({
        account: params.get('acc') || undefined,
        location: params.get('co') || undefined,
        department: params.get('dept') || undefined,
        ...reportParamsFromUrl(params),
        navSeq: prev.navSeq,
      }));
    };

    const handleNavigate = (event: CustomEvent) => {
      const { view, account, location, department, report, reportView, section } = event.detail;
      if (view) {
        setCurrentView(view);
        // A link to a report (My Work → Time Clock › Approvals) puts it in the
        // address, so Back returns to where it came from and a reload stays.
        if (view === 'reports' && report) writeReportAddress(report, reportView);
        // navSeq increments so a target already mounted still reacts — without
        // it, clicking the same deep link twice is a no-op after the viewer has
        // navigated away inside that screen.
        setNavigationParams((prev) => ({
          account,
          location,
          department,
          report,
          reportView,
          section,
          navSeq: (prev.navSeq ?? 0) + 1,
        }));
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('navigate', handleNavigate as EventListener);

    // Set initial state based on URL if authenticated
    if (isAuthenticated) {
      const params = new URLSearchParams(window.location.search);
      let initialView = (params.get('view') as AppView | null) || null;

      if (initialView && !signedInOnThisPageRef.current) {
        // Already signed in on arrival: the address is the destination. Drop
        // what the mount effect saved, or it would hijack the next sign-in in
        // this tab (after sign-out) or a later visit to the bare address.
        clearPostLoginRedirect();
      }

      // If no view in URL (e.g., magic link redirected without query params),
      // check sessionStorage for a saved deep link from before the auth flow
      if (!initialView) {
        try {
          const savedParams = takePostLoginRedirect();
          if (savedParams) {
            const savedView = savedParams.get('view') as AppView | null;
            if (savedView) {
              initialView = savedView;
              // Restore all params from saved redirect
              savedParams.forEach((value, key) => {
                if (!params.has(key)) {
                  params.set(key, value);
                }
              });
            }
          }
        } catch {}
      }

      const resolvedView = initialView || 'dashboard';
      if (!initialView) signInLanding.begin();
      // Don't modify URL if user is on a public page that should work even
      // when authenticated. Resolve from the legacy ?page= query AND the
      // clean pathname (the nav links clean paths like /demo now).
      const currentPage = params.get('page') ?? resolvePathnameToPage();
      //
      // 'supportTicket' MUST be here. Ticket intake requires a login, so the
      // requester is almost always signed in when they click their own emailed
      // portal link — without this the link resolves correctly and is then
      // immediately rewritten to the dashboard, which looks exactly like a
      // broken link.
      //
      // 'share' likewise: a tokenized share link has to render whoever opens it.
      const publicPagesForAuthenticatedUsers = ['invitation-accept', 'sms-terms', 'demo', 'supportTicket', 'share', 'partner-docs'];
      if (currentPage && publicPagesForAuthenticatedUsers.includes(currentPage)) {
        // Let the render function handle these pages - don't redirect to dashboard
        return;
      }
      if (!window.history.state || window.history.state.view !== resolvedView) {
        setCurrentView(resolvedView);
        // Remove 'page' parameter when authenticated - it should not persist
        params.delete('page');
        params.set('view', resolvedView);
        const url = `${authedPathname()}?${params.toString()}`;
        window.history.replaceState({ view: resolvedView }, '', url);
      }

      // Set initial navigation params
      setNavigationParams({
        account: params.get('acc') || undefined,
        location: params.get('co') || undefined,
        department: params.get('dept') || undefined,
        ...reportParamsFromUrl(params),
      });
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('navigate', handleNavigate as EventListener);
    };
  }, [isAuthenticated]);

  // Auto-select the first account for system admins once the query data
  // arrives. The query itself no longer depends on selectedAccountId, so the
  // auto-select (and later account switches) no longer re-issue
  // the account-list fetches the way the old effect did.
  useEffect(() => {
    if (!sysAdminAccountsEnabled || !sysAdminAccounts) return;
    if (selectedAccountId) return;
    const first = sysAdminAccounts.find((a) => usableAccount(a.value));
    if (first) selectAccount(first.value);
  }, [sysAdminAccountsEnabled, sysAdminAccounts, selectedAccountId, selectAccount, usableAccount]);

  // Surface accounts-load failures (parity with the old effect's catch block).
  useEffect(() => {
    if (!sysAdminAccountsEnabled || !sysAdminAccountsError) return;
    console.error('Failed to load accounts:', sysAdminAccountsError);
    const errorMsg = sysAdminAccountsError instanceof Error ? sysAdminAccountsError.message : 'Failed to load accounts';
    // Only show error if it's not a 401 (which is handled by auth)
    if (!errorMsg.includes('401') && !errorMsg.includes('Unauthorized')) {
      setAdminLoadError('Unable to load accounts. Please refresh the page or try again later.');
    }
  }, [sysAdminAccountsEnabled, sysAdminAccountsError]);

  // Auto-select the initial account for non-admin users. useMultiAccountOptions
  // computes the branch-appropriate target (first account with counts, primary
  // account when derived from locations, or the lone JWT account).
  useEffect(() => {
    if (!multiAccountEnabled) return;
    const initial = usableAccount(multiAccountInitialId);
    if (!selectedAccountId && initial) {
      selectAccount(initial);
    }
  }, [multiAccountEnabled, multiAccountInitialId, selectedAccountId, selectAccount, usableAccount]);

  // Set initial account for users with account-scope roles (not system admins who have account selector)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (isSystemAdmin) return; // System admins use the account selector
    if (!canAccessAccountFeatures) return; // Only for users with account-level access
    if (selectedAccountId) return; // Already selected

    // Get account from user data
    const userAccountId =
      usableAccount((user as any).account_id) || userAccounts.map((a: any) => usableAccount(a?.id || a?.account_id)).find(Boolean) || null;

    if (userAccountId) {
      selectAccount(userAccountId);
    }
  }, [isAuthenticated, user, isSystemAdmin, canAccessAccountFeatures, selectedAccountId, userAccounts, selectAccount, usableAccount]);

  // Move the session to a location: the cookie follows the location AND its
  // account, permissions are those at the new location, and the location
  // lists are refreshed. The location switchers (through the provider), the
  // ?co= session sync and the stale-selection fallback all come through here.
  const switchToLocation = useCallback(
    async (locationId: string): Promise<LocationSwitchOutcome> => {
      const previousLocationId = selectedLocationId;
      // Same serialization as the account switch: both mutate
      // one session, and two in flight can finish out of order.
      if (accountSwitchDeps.isSwitchInFlight()) return 'busy';
      accountSwitchDeps.setSwitchInFlight(true);
      try {
        // One switch for everyone (admins included): the cookie
        // moves to the location AND its account, and the server
        // says which account that is — a location picked from
        // another account can't leave the old account showing.
        const { context, permissions: newPermissions } = await adminService.switchContext({ locationId });
        if (context.account_id !== selectedAccountId) {
          selectAccount(context.account_id);
        }
        // Always apply — even an empty set — so fewer permissions
        // at the new location hide gated UI.
        const previousPermissions = permissionsRef.current;
        updateSessionPermissions(newPermissions, locationId);
        selectLocation(locationId);
        // Only when the account or the permissions changed: otherwise the
        // cached lists are already the server's answer, and refreshing them
        // re-fetched the selector list on almost every page load.
        const rescoped = refreshLocationListsAfterSwitch(queryClient, {
          previousAccountId: selectedAccountId,
          accountId: context.account_id,
          previousPermissions,
          permissions: newPermissions,
        });
        // What you may give can change with the session's account and permissions.
        if (rescoped) clearRoleLocks(queryClient);
        return 'ok';
      } catch (err) {
        console.error('Failed to switch location:', err);
        // Do NOT switch location on failure - user doesn't have access
        selectLocation(previousLocationId);
        return 'failed';
      } finally {
        accountSwitchDeps.setSwitchInFlight(false);
      }
    },
    [selectedLocationId, selectedAccountId, accountSwitchDeps, selectAccount, updateSessionPermissions, selectLocation, queryClient]
  );

  // A stable switch for screens below the provider (a work order opened from
  // another of the user's accounts moves there, then loads). Waits out a
  // switch already in flight — the ?co= session sync often is one when a
  // link opens — rather than reporting busy as a failure.
  const switchToLocationRef = useRef(switchToLocation);
  switchToLocationRef.current = switchToLocation;
  const switchToLocationForScreens = useCallback(async (locationId: string): Promise<boolean> => {
    for (let attempt = 0; attempt < 20; attempt++) {
      const outcome = await switchToLocationRef.current(locationId);
      if (outcome !== 'busy') return outcome === 'ok';
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return false;
  }, []);

  // Keep the header on the session's location: a selection restored from
  // ?co= or storage (per browser, not per person) only moved the label, so
  // the header could show a location whose permissions the session did not
  // carry. A mismatch is a real switch; a refused one shows the session's own.
  const { recordSessionLocation } = useSessionLocationSync({
    enabled: isAuthenticated && !!user,
    tokenLocationId: user ? ((user as any).location_id ?? null) : undefined,
    selectedLocationId,
    switchInFlight: accountSwitchInFlight,
    switchToLocation,
    showLocation: selectLocation,
  });
  recordSessionLocationRef.current = recordSessionLocation;

  const staleFallbackRef = useRef<string | null>(null);
  // Auto-select the first (busiest) location once the account's locations
  // arrive (users with account-level access). The query is keyed by account,
  // so location switches and the auto-select itself no longer refetch the list.
  useEffect(() => {
    if (!accountLocationsEnabled) return;
    const options = accountLocationsQuery.data?.options;
    if (!options) return;
    // Nothing selected — or a selection this account does not have (left
    // over from another account in the URL or storage) — lands on the
    // account's first location, so no page shows a location-less view.
    if (options.length === 0) {
      // An account with no locations: nothing from another account may stay selected.
      if (selectedLocationId) selectLocation(null);
      return;
    }
    if (!selectedLocationId) {
      // Prefer the location the session is already on, so landing does not
      // cost a switch (useSessionLocationSync re-scopes any other pick).
      const tokenLocationId = (user as any)?.location_id as string | undefined;
      const onSession = tokenLocationId && options.some((o) => o.value === tokenLocationId);
      selectLocation(onSession ? tokenLocationId : options[0].value);
    } else if (!options.some((o) => o.value === selectedLocationId)) {
      // Left over from another account: a real switch (session + permissions),
      // tried once per stale selection so a failure cannot loop.
      if (staleFallbackRef.current === selectedLocationId) return;
      staleFallbackRef.current = selectedLocationId;
      void switchToLocation(options[0].value);
    }
  }, [accountLocationsEnabled, accountLocationsQuery.data, selectedLocationId, selectLocation, switchToLocation, user]);

  // A selected account that is not available here — from ?acc= or local
  // storage: a production link opened on dev, a deleted account, one the user
  // left — answers 404/403 for its locations. That is a stale selection, not
  // an outage: move to an account the user can use and say so, rather than
  // leave the page stuck on "Unable to load locations" (see utils/staleAccount).
  const toast = useOptionalToast();
  const unavailableAccountsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!accountLocationsEnabled || !accountLocationsQuery.error) return;
    const failedAccountId = effectiveAccountId;
    if (failedAccountId && isUnavailableAccountError(accountLocationsQuery.error)) {
      if (unavailableAccountsRef.current.has(failedAccountId)) return; // already handling it
      unavailableAccountsRef.current.add(failedAccountId);
      setUnavailableAccounts(new Set(unavailableAccountsRef.current));
      const fallback = pickFallbackAccount(
        [...(isSystemAdmin ? [] : [multiAccountInitialId, (user as any)?.account_id]), ...accounts.map((a) => a.value)],
        unavailableAccountsRef.current
      );
      if (!fallback) {
        // Nothing to move to yet (the account lists may still be loading):
        // clear the selection and let the default-account effects pick one.
        selectAccount(null, { replace: true });
        selectLocation(null);
        if (announceUnavailableAccount(window.location.search)) toast?.info('That account isn’t available to you here.');
        return;
      }
      const name = accounts.find((a) => a.value === fallback)?.label;
      void performAccountSwitch(accountSwitchDeps, fallback).then((result) => {
        // ok with an error means the switch happened but the new account's
        // locations or permissions did not load: say that, not "all good".
        const moved = `That account isn’t available to you here${name ? ` — showing ${name} instead` : ''}.`;
        if (result.ok && !result.error) {
          if (announceUnavailableAccount(window.location.search)) toast?.info(moved);
        } else if (result.ok) {
          setAdminLoadError(`${moved} ${result.error}`);
        } else {
          setAdminLoadError(result.error || 'Unable to load locations. Please refresh the page or try again later.');
        }
      });
      return;
    }
    console.error('Failed to load locations:', accountLocationsQuery.error);
    const errorMsg = accountLocationsQuery.error instanceof Error ? accountLocationsQuery.error.message : 'Failed to load locations';
    // Only show error if it's not a 401 (which is handled by auth)
    if (!errorMsg.includes('401') && !errorMsg.includes('Unauthorized')) {
      setAdminLoadError('Unable to load locations. Please refresh the page or try again later.');
    }
    // Deliberately keyed on the error alone: the ids and helpers it reads are
    // current when an error lands, and re-running on their changes would
    // re-handle the same failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountLocationsEnabled, accountLocationsQuery.error]);

  // Auto-select account/location for users with only location-level access
  // (e.g., Hourly Employees with multi-location roles). The /auth/my-locations
  // fetch happens once in useMyLocations; account/location switches only
  // re-run this selection logic against the cached data instead of refetching.
  useEffect(() => {
    if (!myLocationsEnabled || !myLocationsData) return;

    if (myLocationsData.isFallback) {
      // Single-item fallback synthesized from the user's own location_id
      const userLocationId = (user as any).location_id;
      if (!userLocationId) return;
      if (!selectedLocationId) {
        selectLocation(userLocationId);
      }
      if (!selectedAccountId) {
        const userAccountId = usableAccount((user as any).account_id);
        if (userAccountId) {
          selectAccount(userAccountId);
        }
      }
      return;
    }

    if (myLocationsData.options.length === 0) return;

    // `locations` is already filtered by the selected account (memo above)
    const locationsArray = locations;

    // Auto-select first location when account changes and current selection is not in new list
    if (selectedAccountId && locationsArray.length > 0) {
      const currentLocationInList = locationsArray.some((l) => l.value === selectedLocationId);
      if (!currentLocationInList) {
        selectLocation(locationsArray[0].value);
      }
    }

    // Set initial selected account if not already set
    if (!selectedAccountId) {
      const userAccountId = usableAccount((user as any).account_id) || usableAccount(myLocationsData.firstAccountId);
      if (userAccountId) {
        selectAccount(userAccountId);
      }
    }

    // Set initial selected location if not already set
    if (!selectedLocationId && locationsArray.length > 0) {
      const initialId = (user as any).location_id || locationsArray[0].value;
      selectLocation(initialId);
    }
  }, [
    myLocationsEnabled,
    myLocationsData,
    locations,
    user,
    selectedAccountId,
    selectedLocationId,
    selectAccount,
    selectLocation,
    usableAccount,
  ]);

  // Parity with the old effect's catch logging (getMyLocations rarely throws).
  useEffect(() => {
    if (myLocationsEnabled && myLocationsQuery.error) {
      console.error('Failed to load user locations with counts:', myLocationsQuery.error);
    }
  }, [myLocationsEnabled, myLocationsQuery.error]);

  // The account switch deps, read at call time by the account settings link.
  const accountSwitchDepsRef = useRef(accountSwitchDeps);
  useEffect(() => {
    accountSwitchDepsRef.current = accountSwitchDeps;
  }, [accountSwitchDeps]);
  // The account settings link already followed (switched + opened), so a
  // re-run of the URL sync does not switch again.
  const followedAccountLinkRef = useRef<AccountSettingsLinkFollower>(createAccountSettingsLinkFollower());
  const dropAccountSettingsLink = useCallback(() => {
    resetAccountSettingsLinkFollower(followedAccountLinkRef.current);
    setAccountSettingsLink(null);
    // An open dialog takes its own target out of the address as it closes
    // (useSettingsDialogUrl); only a link that never became a dialog is
    // stripped here.
    const link = parseAccountSettingsLink(window.location.search);
    if (link && !isSettingsDialogHeld('account', link.accountId)) {
      const qs = stripAccountSettingsLink(window.location.search);
      window.history.replaceState(window.history.state, '', `${authedPathname()}${qs}`);
    }
  }, []);

  // Sync URL parameters with account/location selectors
  useEffect(() => {
    if (!isAuthenticated) return;

    const params = new URLSearchParams(window.location.search);
    const urlAccountId = params.get('acc');
    const urlLocationId = params.get('co');
    const accountLink = parseAccountSettingsLink(params);

    // An account settings link does a full account switch below (session,
    // location, permissions); moving only the local selection here would
    // run ahead of the cookie.
    if (!accountLink) {
      // Update account selector if URL has account ID and it's different from current selection
      if (urlAccountId && urlAccountId !== selectedAccountId) {
        selectAccount(urlAccountId);
      }

      // Update location selector if URL has location ID and it's different from current selection
      if (urlLocationId && urlLocationId !== selectedLocationId) {
        selectLocation(urlLocationId);
      }
    }

    // ?tab= (+ subtab=) on the location dashboard, or anywhere with
    // settings=location, opens that location's settings on the named tab.
    // A settings dialog already open (Back from a dialog stacked on it, or
    // one a dashboard opened itself) is left to handle the new address.
    const locationLink = parseLocationSettingsLink(params);
    setLocationSettingsLink((prev) => nextShellSettingsLink(prev, locationLink, 'location', (l) => l.locationId));
    // ?account_tab= (+ partner_connection= / partner_tab=) opens the account
    // settings on that tab over whatever view is showing — AFTER the session
    // has been switched to that account (followAccountSettingsLink).
    if (!accountLink) {
      setAccountSettingsLink((prev) => nextShellSettingsLink(prev, null, 'account', (l) => l.accountId));
    } else if (isSettingsDialogHeld('account', accountLink.accountId)) {
      // Already showing (its own address, or Back from a dialog stacked on it),
      // or just closed and still taking itself out of the address.
    } else {
      void followAccountSettingsLinkOnce(followedAccountLinkRef.current, accountLink, {
        switchAccount: (id) => performAccountSwitch(accountSwitchDepsRef.current, id),
        open: setAccountSettingsLink,
        fail: (message) => {
          toast?.error(message);
          dropAccountSettingsLink();
        },
      });
    }
  }, [navigationParams, isAuthenticated]);

  const closeLocationSettingsLink = useCallback(() => {
    setLocationSettingsLink(null);
    // Drop tab/subtab so back / reload lands on the plain dashboard — unless
    // the dialog opened, in which case it hands the address back itself.
    const link = parseLocationSettingsLink(window.location.search);
    if (link && !isSettingsDialogHeld('location', link.locationId)) {
      const qs = stripLocationSettingsLink(window.location.search);
      window.history.replaceState(window.history.state, '', `${authedPathname()}${qs}`);
    }
  }, []);

  // In-app ways into settings (user menu, Location dashboard gear, ⌘K):
  // open the dialog over the current view; it writes its own address. No
  // account switch — the same as the Executive dashboard's buttons; only a
  // link arriving from outside re-scopes the session first. One settings
  // dialog at a time from here: a second request while one is up (⌘K works
  // over dialogs) is ignored rather than replacing it and its unsaved edits.
  useEffect(() => {
    const onOpenSettings = (event: Event) => {
      const target = (event as CustomEvent<SettingsTarget>).detail;
      if (!target) return;
      // A dialog that just closed (switching location) may still be stepping
      // back off its history entry; open once that has landed.
      whenSettingsHistoryIdle(() => openSettingsTarget(target));
    };
    const openSettingsTarget = (target: SettingsTarget) => {
      if (isAnySettingsDialogOpen()) return;
      if (target.kind === 'account') {
        setAccountSettingsLink({
          accountId: target.accountId,
          tab: target.tab || 'details',
          subTab: target.subTab,
          section: target.section,
        });
      } else {
        setLocationSettingsLink({
          locationId: target.locationId,
          accountId: target.accountId,
          tab: target.tab || 'details',
          subTab: target.subTab,
        });
      }
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
  }, []);

  const closeAccountSettingsLink = useCallback(() => dropAccountSettingsLink(), [dropAccountSettingsLink]);

  // Setup fetch interceptor to handle 431 errors
  useEffect(() => {
    setupFetchInterceptor();

    return () => {
      teardownFetchInterceptor();
    };
  }, []);

  // Cookie size monitoring and cleanup
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check cookie size on mount
    const initialSize = getTotalCookieSize();
    if (initialSize > 4096) {
      console.warn('⚠️ Large cookie detected on app load');
      logCookieDiagnostics();

      // Auto-cleanup duplicates
      cleanupDuplicateCookies();

      // Show recommendations
      const recommendations = getCookieRecommendations();
      if (recommendations.length > 0) {
        console.warn('Cookie Recommendations:');
        recommendations.forEach((r) => console.warn(r));
      }
    }

    // Set up periodic monitoring (every 60 seconds)
    const intervalId = setInterval(() => {
      const currentSize = getTotalCookieSize();

      if (currentSize > 8192) {
        console.error('🚨 CRITICAL: Cookie size exceeds 8KB - this may cause 431 errors!');
        logCookieDiagnostics();
      } else if (currentSize > 6144) {
        console.warn('⚠️ WARNING: Cookie size approaching limit (6KB+)');
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // Check for saved deep link redirect after login
  const navigateAfterLogin = () => {
    try {
      const savedParams = takePostLoginRedirect();
      if (savedParams) {
        let targetView = savedParams.get('view') as AppView;

        if (targetView) {
          // Check if user has permission to view account dashboard
          // Location-level users should be redirected to location dashboard instead
          if (targetView === 'dashboard:accounts' && !showExecutiveDashboard) {
            targetView = 'dashboard:location';
            savedParams.set('view', targetView);
          }

          // Clean up incomplete deep links:
          // - If there's a noteId but no wo (work order ID), the deep link is incomplete
          //   and we can't show the specific note, so remove the noteId
          if (savedParams.get('noteId') && !savedParams.get('wo')) {
            savedParams.delete('noteId');
          }

          // For location-level users going to workorders without a specific work order,
          // redirect to their default dashboard instead
          if (targetView === 'workorders' && !savedParams.get('wo') && !canAccessAccountFeatures) {
            // Clear stale localStorage and go to dashboard
            try {
              localStorage.removeItem('current_view');
            } catch {}
            navigateToDashboard();
            return;
          }

          setCurrentView(targetView);
          // Update navigation params from saved redirect
          setNavigationParams({
            account: savedParams.get('acc') || undefined,
            location: savedParams.get('co') || undefined,
            department: savedParams.get('dept') || undefined,
            ...reportParamsFromUrl(savedParams),
          });
          // Remove the 'page' parameter to avoid re-entering public page flow
          savedParams.delete('page');
          const cleanedParams = savedParams.toString();
          // Ensure proper URL construction with ? separator (never &)
          const newUrl = cleanedParams ? `${authedPathname()}?${cleanedParams}` : authedPathname();
          // Update URL with the cleaned params
          window.history.replaceState({ view: targetView }, '', newUrl);
          try {
            localStorage.setItem('current_view', JSON.stringify(targetView));
          } catch {}
          return; // Don't navigate to dashboard
        }
      }
    } catch {}
    // Fallback to dashboard if no saved redirect - clear any stale localStorage
    try {
      localStorage.removeItem('current_view');
    } catch {}
    navigateToDashboard();
  };

  const handleLogin = async (creds: { email?: string; username?: string; password: string }) => {
    signedInOnThisPageRef.current = true;
    try {
      const response = await login(creds);

      // Set pending terms from login response (no separate API call needed)
      const loginResponse = response as any;
      if (loginResponse.has_pending_terms !== undefined) {
        setPendingTermsFromLogin(loginResponse.pending_terms, loginResponse.has_pending_terms);

        // Only navigate if no pending terms
        if (!loginResponse.has_pending_terms) {
          navigateAfterLogin();
        }
        // If there are pending terms, the modal will show automatically
      } else {
        // Fallback: if backend doesn't return terms info, navigate after login
        navigateAfterLogin();
      }
    } catch (err) {
      // Forced password reset is a typed error — bubble it so LoginScreen
      // can pivot into the reset panel. Other auth failures are already
      // surfaced through useAuth.error.
      if ((err as any)?.code === 'FORCE_PASSWORD_RESET_REQUIRED') {
        throw err;
      }
    }
  };

  // Handle passwordless login success (OTP verification)
  const handlePasswordlessSuccess = (response: any) => {
    signedInOnThisPageRef.current = true;
    // Sync React auth state from the OTP verify response (cookie is already set by backend)
    loginFromPasswordless(response);
    const reset = pendingResetFromGrant(response);
    if (reset) setPendingPasswordReset(reset);

    // Check for redirect from magic link first
    if (response.redirect_path) {
      // Clean the redirect path - strip leading ? or & for proper parsing
      const cleanPath = response.redirect_path.replace(/^[?&]/, '');
      const redirectParams = new URLSearchParams(cleanPath);
      let targetView = redirectParams.get('view') as AppView;
      if (targetView) {
        // Check if user has permission to view account dashboard
        // Location-level users should be redirected to location dashboard instead
        if (targetView === 'dashboard:accounts' && !showExecutiveDashboard) {
          targetView = 'dashboard:location';
          redirectParams.set('view', targetView);
        }

        setCurrentView(targetView);
        setNavigationParams({
          account: redirectParams.get('acc') || undefined,
          location: redirectParams.get('co') || undefined,
          department: redirectParams.get('dept') || undefined,
          ...reportParamsFromUrl(redirectParams),
        });
        // Remove 'page' parameter to avoid re-entering public page flow
        redirectParams.delete('page');
        const cleanedParams = redirectParams.toString();
        // Ensure proper URL construction with ? separator (never &)
        const newUrl = cleanedParams ? `${authedPathname()}?${cleanedParams}` : authedPathname();
        window.history.replaceState({ view: targetView }, '', newUrl);
        // The magic link's own target wins; nothing saved earlier may linger.
        clearPostLoginRedirect();
        return;
      }
    }
    // Otherwise use standard post-login navigation (checks sessionStorage)
    navigateAfterLogin();
  };

  // Navigate to dashboard after login (or after terms acceptance)
  const navigateToDashboard = () => {
    setCurrentView('dashboard');
    try {
      // Drops `page` (no re-entering the public page flow) and anything that
      // would re-open a work order, report or invoice batch on the dashboard.
      window.history.replaceState({ view: 'dashboard' }, '', `${authedPathname()}?${dashboardSearch(window.location.search)}`);
      try {
        localStorage.setItem('current_view', JSON.stringify('dashboard'));
      } catch {}
    } catch {}
  };

  // Handle completion of terms acceptance
  const handleTermsAccepted = () => {
    clearTermsState();
    navigateAfterLogin(); // Restore deep link if one was saved
  };

  const handleLogout = async () => {
    // A link remembered in this tab belongs to whoever opened it.
    clearPostLoginRedirect();
    clearRoleLocks(queryClient);
    await logout();
    // Redirect to login page
    window.location.href = `${window.location.pathname}?page=login`;
  };

  // Sign out from a work order or invoice this login can't open (an emailed
  // link): keep the link, so the sign-in page names it and the right login
  // lands on it.
  const handleSignOutKeepingLink = async () => {
    const link = recordLinkOnly(window.location.search);
    clearPostLoginRedirect();
    await logout();
    window.location.href = link ? `${window.location.pathname}?${link}` : `${window.location.pathname}?page=login`;
  };

  // (removed unused handleNavigation)

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    window.history.pushState({ view: 'dashboard' }, '', authedPathname());
  };

  // Public pages that don't require authentication.
  // Priority: legacy ?page=X query → clean URL pathname → none.
  //
  // Query wins because the top-nav links in HomePage/SupportPage/etc.
  // still use ?page= for navigation. If pathname resolution won, every
  // nav link click (which lands on /web/?page=X with pathname /web/)
  // would route to home because the bare /web/ pathname resolves to
  // 'home' via the route registry. Phase 2 work added clean URLs as
  // an additional surface, not a replacement for the legacy ?page=
  // routing — they coexist until the nav links are migrated.
  const params = new URLSearchParams(window.location.search);
  const publicPage = params.get('page') ?? resolvePathnameToPage();

  // Public page loading fallback
  const publicPageFallback = (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--tc-background-primary, #000000)',
        color: 'var(--tc-text-primary, #FFFFFF)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '3px solid var(--tc-border-light, #333333)',
            borderTop: '3px solid var(--tc-border-focus, #007AFF)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px auto',
          }}
        />
        <div>Loading...</div>
      </div>
    </div>
  );

  // Handle public pages
  if (publicPage === 'home') {
    return (
      <Suspense fallback={publicPageFallback}>
        <HomePage />
      </Suspense>
    );
  }
  // /demo (and legacy ?page=demo) renders the dedicated booking page from
  // the marketing-site design.
  if (publicPage === 'demo') {
    return (
      <Suspense fallback={publicPageFallback}>
        <DemoPage />
      </Suspense>
    );
  }
  if (publicPage === 'solutions') {
    return (
      <Suspense fallback={publicPageFallback}>
        <SolutionsPage />
      </Suspense>
    );
  }
  if (publicPage === 'integrations') {
    return (
      <Suspense fallback={publicPageFallback}>
        <IntegrationsPage />
      </Suspense>
    );
  }
  // Public Support page: the FAQ + support-email contact. (The in-app Help
  // Center keeps the integration setup guides.) Rendered for everyone, like
  // the other public pages.
  if (publicPage === 'support') {
    return (
      <Suspense fallback={publicPageFallback}>
        <SupportPage />
      </Suspense>
    );
  }
  if (publicPage === 'share') {
    return (
      <Suspense fallback={publicPageFallback}>
        <SharedResourcePage token={resolveShareTokenFromPath() ?? ''} />
      </Suspense>
    );
  }
  if (publicPage === 'supportTicket') {
    return (
      <Suspense fallback={publicPageFallback}>
        <SupportTicketPage token={resolveSupportTokenFromPath() ?? ''} />
      </Suspense>
    );
  }
  if (publicPage === 'guide') {
    const slug = params.get('id') || resolveGuideIdFromPath() || '';
    // The guide (and the section a link pointed at) inside the Help Center.
    const target = helpGuideSearch(slug, params.get('section'));
    // Wait until we know whether this browser is signed in: deciding while
    // the session is still being checked sent signed-in readers to the home page.
    if (isLoading) return publicPageFallback;
    if (isAuthenticated) {
      window.location.replace(`/${target}`);
    } else {
      // Signed out: remember the guide so signing in lands on it.
      try {
        sessionStorage.setItem('post_login_redirect', target.replace(/^\?/, ''));
      } catch {}
      window.location.replace('/?page=home');
    }
    return publicPageFallback;
  }
  if (publicPage === 'partner-docs') {
    return (
      <Suspense fallback={publicPageFallback}>
        <PartnerDocs />
      </Suspense>
    );
  }
  if (publicPage === 'privacy') {
    return (
      <Suspense fallback={publicPageFallback}>
        <PrivacyPage />
      </Suspense>
    );
  }
  if (publicPage === 'sms-terms') {
    return (
      <Suspense
        fallback={
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--tc-background-default, #FFFFFF)',
            }}
          >
            Loading...
          </div>
        }
      >
        <SmsTermsPage />
      </Suspense>
    );
  }

  // Handle invitation acceptance page
  if (publicPage === 'invitation-accept') {
    return (
      <Suspense fallback={publicPageFallback}>
        <InvitationAcceptPage />
      </Suspense>
    );
  }

  // Segment landing deep-links — render the Solutions overview with
  // per-route SEO heads (dedicated copy to be added per
  // docs/UI_OPTIMIZATION_ROADMAP.md as case studies become available)
  if (publicPage === 'solutions/dealer-groups') {
    return (
      <Suspense fallback={publicPageFallback}>
        <DealerGroupsPage />
      </Suspense>
    );
  }
  if (publicPage === 'solutions/mobile-repair') {
    return (
      <Suspense fallback={publicPageFallback}>
        <MobileRepairPage />
      </Suspense>
    );
  }
  if (publicPage === 'solutions/fleet-service') {
    return (
      <Suspense fallback={publicPageFallback}>
        <FleetServicePage />
      </Suspense>
    );
  }
  if (publicPage === 'solutions/quick-service') {
    return (
      <Suspense fallback={publicPageFallback}>
        <QuickServicePage />
      </Suspense>
    );
  }
  if (publicPage === 'solutions/independent-shops') {
    return (
      <Suspense fallback={publicPageFallback}>
        <IndependentShopsPage />
      </Suspense>
    );
  }

  // Phase-2 integration landing pages
  if (publicPage === 'integrations/netsuite') {
    return (
      <Suspense fallback={publicPageFallback}>
        <NetSuiteIntegrationPage />
      </Suspense>
    );
  }
  if (publicPage === 'integrations/quickbooks') {
    return (
      <Suspense fallback={publicPageFallback}>
        <QuickBooksIntegrationPage />
      </Suspense>
    );
  }

  // Handle login page (bookmarkable)
  if (publicPage === 'login') {
    // Check if a magic link authenticated a different user than was previously logged in
    if (isAuthenticated && user && !userSwitchPending) {
      const isDifferentUser = preAuthUser?.id && user.id && String(preAuthUser.id) !== String(user.id);

      if (isDifferentUser) {
        // Show switch prompt instead of auto-navigating
        setUserSwitchPending({ oldUser: preAuthUser, newUser: user });
      } else {
        // Same user or no previous user - proceed normally
        navigateAfterLogin();
        // Show skeleton matching dashboard layout while redirecting to prevent CLS
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              backgroundColor: 'var(--tc-background-primary, #000000)',
              color: 'var(--tc-text-primary, #FFFFFF)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            }}
          >
            <div
              style={{
                minHeight: '3.75rem',
                backgroundColor: 'var(--tc-background-secondary, #0B0B0B)',
                borderBottom: '1px solid var(--tc-border-default, #2A2A1F)',
                flexShrink: 0,
              }}
            />
            <div
              style={{
                minHeight: '3.5rem',
                backgroundColor: 'var(--tc-background-secondary, #0B0B0B)',
                borderBottom: '1px solid var(--tc-border-default, #2A2A1F)',
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    width: '2rem',
                    height: '2rem',
                    border: '3px solid var(--tc-border-light, #333333)',
                    borderTop: '3px solid var(--tc-border-focus, #007AFF)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 1rem auto',
                  }}
                />
                <div>Redirecting...</div>
              </div>
            </div>
          </div>
        );
      }
    }

    // Show user switch modal when a magic link authenticated a different user
    if (userSwitchPending) {
      return (
        <UserSwitchModal
          isOpen={true}
          oldUser={userSwitchPending.oldUser}
          newUser={userSwitchPending.newUser}
          onSwitch={() => {
            // Accept the new user and proceed
            setUserSwitchPending(null);
            navigateAfterLogin();
          }}
          onStay={() => {
            // Reject the switch - log out the new user, stay on login screen
            setUserSwitchPending(null);
            logout();
          }}
        />
      );
    }

    // Handle error and redirect params from expired/invalid magic links
    const loginParams = new URLSearchParams(window.location.search);
    const errorCode = loginParams.get('error');
    const redirectPath = loginParams.get('redirect');

    // Map error codes to user-friendly messages
    let loginError = error;
    if (errorCode === 'link_expired') {
      loginError = 'This link has expired. Please log in to continue to your destination.';
    } else if (errorCode === 'link_used') {
      loginError = 'This link has already been used. Please log in to continue.';
    } else if (errorCode === 'invalid_link') {
      loginError = 'This link is invalid or has expired. Please log in to continue.';
    } else if (errorCode === 'password_reset_required') {
      loginError = 'Your manager asked you to choose a new password. Sign in with a code or your password to set it.';
    }

    // Save redirect path for after successful login
    // Only save record links (wo, noteId, batch) - not general navigation states
    // This prevents stale dashboard redirects from affecting future logins
    if (redirectPath) savePostLoginRedirect(redirectPath);

    return (
      <AccountProvider initialUser={user} initialPermissions={permissions} isAuthenticated={isAuthenticated} isAuthLoading={isLoading}>
        <Suspense fallback={publicPageFallback}>
          <LoginScreen
            isLoading={isLoading}
            error={loginError}
            onSubmit={handleLogin}
            onPasswordlessSuccess={handlePasswordlessSuccess}
            initialForceResetToken={pendingPasswordReset?.required ? pendingPasswordReset.token : undefined}
            onForceResetHandled={() => setPendingPasswordReset(null)}
            fromLink={recordLinkKind(redirectPath)}
          />
        </Suspense>
      </AccountProvider>
    );
  }

  // Loading screen — skeleton matches the authenticated dashboard layout
  // (header 60px + nav 56px + content) to prevent CLS during auth transition
  if (isLoading && !user) {
    return (
      <AccountProvider initialUser={user} initialPermissions={permissions} isAuthenticated={isAuthenticated} isAuthLoading={isLoading}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: 'var(--tc-background-primary, #000000)',
            color: 'var(--tc-text-primary, #FFFFFF)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
          }}
        >
          {/* Header placeholder — matches GlobalHeader height/style */}
          <div
            style={{
              minHeight: '3.75rem',
              backgroundColor: 'var(--tc-background-secondary, #0B0B0B)',
              borderBottom: '1px solid var(--tc-border-default, #2A2A1F)',
              flexShrink: 0,
            }}
          />
          {/* Nav placeholder — matches MainNavigation height/style */}
          <div
            style={{
              minHeight: '3.5rem',
              backgroundColor: 'var(--tc-background-secondary, #0B0B0B)',
              borderBottom: '1px solid var(--tc-border-default, #2A2A1F)',
              flexShrink: 0,
            }}
          />
          {/* Content area with centered spinner */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: '2rem',
                  height: '2rem',
                  border: '3px solid var(--tc-border-light, #333333)',
                  borderTop: '3px solid var(--tc-border-focus, #007AFF)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem auto',
                }}
              />
              <div>Loading...</div>
            </div>
          </div>
        </div>
      </AccountProvider>
    );
  }

  // Show authenticated views with full navigation and sidebar
  if (isAuthenticated && user) {
    // Support super admin "view as" via URL params: as=account|location, acc, co
    const params = new URLSearchParams(window.location.search);
    const viewAs = params.get('as');
    const viewAsAccountId = params.get('acc');
    const viewAsLocationId = params.get('co');
    // Handle navigation functions with history support
    // Both leave an open settings page first (it sits over every view, so a
    // view switched underneath it never shows), asking about unsaved edits.
    const handleEditProfile = () => leaveSettingsAndGo(showProfile);
    const showProfile = () => {
      setCurrentView('profile');
      const params = new URLSearchParams(window.location.search);
      params.set('view', 'profile');
      params.delete('wo');
      params.delete('page'); // Never persist 'page' parameter when authenticated
      window.history.pushState({ view: 'profile' }, '', `${authedPathname()}?${params.toString()}`);
    };

    const handleAdminDashboard = () => leaveSettingsAndGo(showAdminDashboard);
    const showAdminDashboard = () => {
      setCurrentView('admin');
      const params = new URLSearchParams(window.location.search);
      params.set('view', 'admin');
      params.delete('admin'); // Clear admin sub-view to go to main admin dashboard
      params.delete('wo');
      params.delete('page'); // Never persist 'page' parameter when authenticated
      window.history.pushState({ view: 'admin' }, '', `${authedPathname()}?${params.toString()}`);
    };

    // Handle profile save
    const handleProfileSave = async (updatedUser: Partial<User>) => {
      try {
        await updateProfile(updatedUser);
        // Optionally show a success message
        // Profile updated successfully
      } catch (error) {
        console.error('Failed to update profile:', error);
        // Handle error (could show error message to user)
      }
    };

    const renderContent = () => {
      try {
        if (viewAs === 'account') {
          return <ExecutiveDashboard key={executiveKey} user={user} permissions={permissions} />;
        }
        if (viewAs === 'location') {
          return (
            <Dashboard
              user={user}
              permissions={permissions}
              onLogout={handleLogout}
              onEditProfile={handleEditProfile}
              onAdminDashboard={handleAdminDashboard}
              initialLocationId={viewAsLocationId}
              initialAccountId={viewAsAccountId}
            />
          );
        }
        switch (currentView) {
          case 'profile':
            return (
              <UserProfile
                user={user}
                permissions={permissions}
                onBack={handleBackToDashboard}
                onSave={handleProfileSave}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
              />
            );
          case 'admin':
            return (
              <AdminDashboard
                user={user}
                permissions={permissions}
                onBack={handleBackToDashboard}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
              />
            );
          case 'workorders':
            return (
              <WorkOrders
                user={user}
                permissions={permissions}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
                initialAccountId={navigationParams.account}
                initialLocationId={navigationParams.location}
                initialDepartmentId={navigationParams.department}
              />
            );
          case 'customers':
            return (
              <CustomersView
                user={user}
                permissions={permissions}
                accountId={navigationParams.account || effectiveAccountId}
                locationId={navigationParams.location || selectedLocationId}
                locations={uniqueLocations.map((l) => ({ id: l.value, name: l.label }))}
              />
            );
          case 'reports':
            return (
              <Reports
                user={user}
                permissions={permissions}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
                initialReportType={navigationParams.report}
                initialReportView={navigationParams.reportView}
                initialNavSeq={navigationParams.navSeq}
              />
            );
          case 'mywork':
            // Unified actionable hub: approvals, corrections, personal to-dos.
            return (
              <MyWorkPage
                user={user}
                permissions={permissions}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
                accountId={effectiveAccountId || undefined}
                // From the Help Center's ticket links; read from the URL too so
                // the card is still open after a reload or Back.
                initialSection={navigationParams.section || params.get('section') || undefined}
                navSeq={navigationParams.navSeq}
              />
            );
          case 'inventory':
            return (
              <Inventory
                user={user}
                permissions={permissions}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
                accountId={navigationParams.account || selectedAccountId || undefined}
                locationId={navigationParams.location || selectedLocationId || undefined}
              />
            );
          case 'help':
            // In-app Help Center. Opens directly to a guide when a ?guide=
            // slug deep link is present (e.g. from the Integrations setup link).
            return (
              <HelpCenter
                initialSlug={params.get('guide') || undefined}
                initialSection={params.get('section') || undefined}
                userName={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || undefined}
                userEmail={user?.email || undefined}
                userPhone={user?.phone_number || undefined}
              />
            );
          case 'dashboard:accounts':
            // Redirect to the location dashboard if the user can't see the
            // executive dashboard (lacks account features or the permission).
            if (!showExecutiveDashboard) {
              if (!canViewLocationDashboard) {
                return <LocationNoAccess user={user} permissions={permissions} onLogout={handleLogout} />;
              }
              return (
                <Dashboard
                  user={user}
                  permissions={permissions}
                  onLogout={handleLogout}
                  onEditProfile={handleEditProfile}
                  onAdminDashboard={handleAdminDashboard}
                  initialAccountId={navigationParams.account || selectedAccountId || undefined}
                  initialLocationId={navigationParams.location || selectedLocationId || undefined}
                />
              );
            }
            return <ExecutiveDashboard key={executiveKey} user={user} permissions={permissions} />;
          case 'dashboard:location':
            // Gate the location dashboard on the view permission. Fall back to the
            // executive dashboard if that's the only one the user can see.
            if (!canViewLocationDashboard) {
              if (showExecutiveDashboard) {
                return <ExecutiveDashboard key={executiveKey} user={user} permissions={permissions} />;
              }
              return <LocationNoAccess user={user} permissions={permissions} onLogout={handleLogout} />;
            }
            return (
              <Dashboard
                key={`location-${navigationParams.account || selectedAccountId}-${navigationParams.location || selectedLocationId}`}
                user={user}
                permissions={permissions}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
                initialLocationId={navigationParams.location || selectedLocationId || undefined}
                initialAccountId={navigationParams.account || selectedAccountId || undefined}
              />
            );
          case 'dashboard':
          default: {
            // Prefer the executive dashboard when the user can see it; otherwise
            // the location dashboard; otherwise a no-access fallback.
            if (showExecutiveDashboard) {
              return <ExecutiveDashboard key={executiveKey} user={user} permissions={permissions} />;
            }
            if (!canViewLocationDashboard) {
              return <LocationNoAccess user={user} permissions={permissions} onLogout={handleLogout} />;
            }
            // Default to location dashboard for users with only location-scope roles
            return (
              <Dashboard
                user={user}
                permissions={permissions}
                onLogout={handleLogout}
                onEditProfile={handleEditProfile}
                onAdminDashboard={handleAdminDashboard}
                initialAccountId={navigationParams.account}
                initialLocationId={navigationParams.location}
              />
            );
          }
        }
      } catch (e) {
        console.error('[App] renderContent error', e);
        return (
          <div style={{ color: 'var(--tc-text-primary, #FFFFFF)', background: 'var(--tc-background-primary, #000000)', padding: 24 }}>
            Render error: {e instanceof Error ? e.message : String(e)}
          </div>
        );
      }
    };

    // The main navigation leaves an open settings page first, asking about
    // unsaved edits (Keep editing cancels the move).
    const handleNavigate = (v: AppView) => leaveSettingsThen(() => navigateTo(v));
    const navigateTo = (v: AppView) => {
      // If dashboard is selected again, signal any panels to hide
      if (v === 'dashboard' || v.startsWith('dashboard:')) {
        try {
          window.dispatchEvent(new CustomEvent('hideWorkOrdersPanel'));
        } catch {}
      }
      setCurrentView(v);
      try {
        localStorage.setItem('current_view', JSON.stringify(v));
      } catch {}
      // A settings page that just closed takes its own address back out; a
      // view change must not carry it along either.
      const params = new URLSearchParams(stripLocationSettingsLink(stripAccountSettingsLink(window.location.search)));
      params.set('view', v);

      // Always clear work order parameter to show list view
      params.delete('wo');
      // Clear any Help Center deep-link slug so the Help tab opens the index.
      params.delete('guide');
      // Same for My Work's ?section= (the Help Center's link to Help tickets).
      params.delete('section');
      // And a customer's page: the Customers tab opens on the list.
      params.delete('cust');
      // A report (and its sub-tab) belongs to the Reports view only.
      if (v !== 'reports') {
        params.delete('report');
        params.delete('report_view');
      }
      // Always clear 'page' parameter - it should never persist when authenticated
      params.delete('page');
      // Top-nav navigation abandons any in-flight creation intent — a stale
      // create=true would re-open the creation modal on back/reload.
      clearCreateParams(params);
      clearPlaceParams(params, v);

      if (v !== 'workorders') {
        // Keep account/location params for dashboard views
        if (!v.startsWith('dashboard:')) {
          params.delete('acc');
          params.delete('co');
          setNavigationParams({});
        }
      }
      window.history.pushState({ view: v }, '', `${authedPathname()}?${params.toString()}`);
    };

    // Open a global search result — shared by the header search dropdown and
    // the command palette / quick keys (QuickKeys).
    // An open settings page is left first (asking about unsaved edits): it
    // covers the main view, so a work order opened behind it never showed, and
    // a second settings page was refused while one was up.
    const openSearchSelection = (selection: SearchSelection) => leaveSettingsThen(() => showSearchSelection(selection));
    const showSearchSelection = (selection: SearchSelection) => {
      // Accounts, locations and settings sections open the settings dialog
      // over the current view.
      if (selection.type === 'settings') {
        openSettings(selection.target);
        return;
      }
      // A customer opens their page in Customers, at the store searched.
      if (selection.type === 'customer') {
        navigateToCustomers({ accountId: effectiveAccountId, locationId: selection.locationId, customerId: selection.id });
        return;
      }
      // The settings page that just closed takes its own address out; the
      // result's address must not carry it along either.
      const params = new URLSearchParams(stripLocationSettingsLink(stripAccountSettingsLink(window.location.search)));
      params.delete('page'); // Never persist 'page' parameter when authenticated
      // Opening a search result must drop any in-flight creation intent, or
      // useWorkOrderUrlSync re-opens the create modal on top of the result.
      clearCreateParams(params);
      if (selection.type === 'work_order') {
        // Navigate to work orders view with selected work order
        setCurrentView('workorders');
        params.set('view', 'workorders');
        params.set('wo', selection.id);
        params.delete('batch');
        window.history.pushState({ view: 'workorders' }, '', `${authedPathname()}?${params.toString()}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        // invoice_batch — open the batch detail modal inside the Reports view.
        // ReportsDashboard reads the `batch` query param on mount and
        // popstate, finds the row, and opens BatchDetailModal.
        setCurrentView('reports');
        params.set('view', 'reports');
        params.set('tab', 'invoices');
        // The batch lives on Invoices: a report left in the address from
        // earlier (?report=payroll) would otherwise be re-applied and hide it.
        params.set('report', 'invoices');
        params.delete('report_view');
        params.set('batch', selection.id);
        params.delete('wo');
        window.history.pushState({ view: 'reports' }, '', `${authedPathname()}?${params.toString()}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    };

    // "New Work Order" action (nav button, palette, `n` key) — offered to
    // anyone who can create work orders, over the current view, starting at
    // the location on screen (see quickCreateLocationId).
    const canCreateWorkOrder = isSystemAdmin || permissions.includes('workorders:create');
    const quickCreateLocation = quickCreateLocationId({
      currentView,
      showExecutiveDashboard,
      canViewLocationDashboard,
      // The selection follows every switch; the address can lag behind it.
      // The Customers page picks its store in the address alone.
      locationId:
        currentView === 'customers' ? navigationParams.location || selectedLocationId : selectedLocationId || navigationParams.location,
    });
    const quickCreateAccountId = effectiveAccountId && effectiveAccountId !== 'ALL' ? effectiveAccountId : null;
    const quickCreateWorkOrder = canCreateWorkOrder && quickCreateAccountId ? () => setIsQuickCreateOpen(true) : undefined;

    return (
      <AccountProvider initialUser={user} initialPermissions={permissions} isAuthenticated={isAuthenticated} isAuthLoading={isLoading}>
        <AccountSwitchProvider deps={accountSwitchDeps} switchToLocation={switchToLocationForScreens}>
          <SignOutProvider signOut={handleSignOutKeepingLink}>
            <MyWorkProvider accountId={effectiveAccountId}>
              {/* Terms Acceptance Modal - shown after login if there are pending terms */}
              {hasPendingTerms && (
                <TermsAcceptanceModal isOpen={hasPendingTerms} pendingTerms={pendingTerms} onAcceptComplete={handleTermsAccepted} />
              )}

              {/* Only for the user the token was issued to: never offer one person's
                reset to whoever signs in next on this browser. */}
              {!hasPendingTerms && shouldOfferPasswordReset(pendingPasswordReset, user.id) && (
                <SetNewPasswordModal reset={pendingPasswordReset} onDone={() => setPendingPasswordReset(null)} />
              )}

              {/* Admin API error banner - shown when account/location loading fails */}
              {adminLoadError && (
                <div
                  style={{
                    backgroundColor: 'var(--tc-background-default, #FFFFFF)',
                    borderBottom: '1px solid var(--tc-border-required, #FF9800)',
                    color: 'var(--tc-brand-darker, #B3421F)',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '13px',
                  }}
                >
                  <span>{adminLoadError}</span>
                  <button
                    onClick={() => setAdminLoadError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--tc-brand-darker, #B3421F)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                    }}
                    aria-label="Dismiss error"
                  >
                    ×
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                {/* Global Header */}
                <ErrorBoundary>
                  <GlobalHeader
                    user={user}
                    permissions={permissions}
                    onLogout={handleLogout}
                    onEditProfile={handleEditProfile}
                    onAdminDashboard={handleAdminDashboard}
                    onSearchSelect={openSearchSelection}
                    locationId={navigationParams.location || selectedLocationId || null}
                    scopeAccountId={settingsScopeAccountId}
                    // The logo goes home the way the main navigation does:
                    // settings pages close first, then the default dashboard —
                    // at its top: no account, group or search left drilled into.
                    onLogoClick={() =>
                      leaveSettingsThen(() => {
                        setExecResetCount((n) => n + 1);
                        navigateTo('dashboard');
                      })
                    }
                  />
                </ErrorBoundary>

                {/* Global keyboard shortcuts + command palette (Ctrl+K, /, n, g l, ?) */}
                <ErrorBoundary>
                  <QuickKeys
                    onSelect={openSearchSelection}
                    onCreateWorkOrder={quickCreateWorkOrder}
                    locationId={navigationParams.location || selectedLocationId || null}
                    accountId={settingsScopeAccountId}
                    permissions={permissions}
                  />
                </ErrorBoundary>

                {/* A location's settings on one tab: from a notification link
              (tab= / subtab= in the URL), a reload or Forward onto a settings
              address, or an in-app entry point (openSettings). */}
                {locationSettingsLink && (
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <DeepLinkedLocationModal
                        link={locationSettingsLink}
                        user={user}
                        permissions={permissions}
                        onClose={closeLocationSettingsLink}
                        accountName={accounts.find((a) => a.value === (locationSettingsLink.accountId || selectedAccountId))?.label}
                        switchableLocations={uniqueLocations.map((l) => ({ id: l.value, name: l.label }))}
                      />
                    </Suspense>
                  </ErrorBoundary>
                )}

                {/* The account's settings on one tab, from a notification link
              (account_tab= / partner_connection= in the URL). */}
                {accountSettingsLink && (
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <DeepLinkedAccountModal
                        link={accountSettingsLink}
                        user={user}
                        permissions={permissions}
                        onClose={closeAccountSettingsLink}
                        accountName={accounts.find((a) => a.value === accountSettingsLink.accountId)?.label}
                      />
                    </Suspense>
                  </ErrorBoundary>
                )}

                {/* App-level create-work-order modal — opens over the current view.
              Mounted only while open so the lazy chunk loads on first use and
              the modal's internal state resets between opens. */}
                {isQuickCreateOpen && quickCreateAccountId && (
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <WorkOrderCreateModal
                        isOpen={isQuickCreateOpen}
                        onClose={() => setIsQuickCreateOpen(false)}
                        onCreated={(workOrderId: string, departmentId?: string, createdAtLocationId?: string) => {
                          // Where it was created — the form's location band may have changed it.
                          const createdLocationId = createdAtLocationId || quickCreateLocation || selectedLocationId || '';
                          setIsQuickCreateOpen(false);
                          // Views showing work-order data (location dashboard
                          // counts, work-order list) subscribe to this and refresh
                          // after the backend's async routing listeners settle —
                          // see utils/workOrderEvents.ts.
                          if (createdLocationId) announceWorkOrderCreated({ workOrderId, locationId: createdLocationId, departmentId });
                          // Mirror the work-order-list create flow: when routing
                          // assigned a department, land on the location dashboard
                          // with that department highlighted. (From the dashboard
                          // itself this is an in-place highlight, not a view
                          // change.) Without a department, stay where we are — the
                          // modal's own success toast links to the new WO.
                          if (departmentId && createdLocationId) {
                            navigateToLocationDashboard(quickCreateAccountId, createdLocationId, { departmentId });
                          }
                        }}
                        initialAccountId={quickCreateAccountId}
                        initialLocationId={quickCreateLocation}
                        permissions={permissions}
                        user={user}
                      />
                    </Suspense>
                  </ErrorBoundary>
                )}

                {/* Main Navigation */}
                <ErrorBoundary>
                  <MainNavigation
                    currentView={currentView}
                    onNavigate={handleNavigate}
                    onCreateWorkOrder={quickCreateWorkOrder}
                    user={user}
                    permissions={permissions}
                    selectedAccountId={selectedAccountId}
                    selectedLocationId={selectedLocationId}
                    hasLocations={uniqueLocations.length > 0}
                    canSwitchLocation={uniqueLocations.length > 1 || ((user as any)?.accounts?.length ?? 0) > 1}
                  />
                </ErrorBoundary>

                {/* Main Content. Account and Location settings render as a page
              into #settings-page-host beside it (SettingsPageFrame); while one
              is up the view underneath is hidden but stays mounted, and when two
              stack (a location opened from its account) only the latest shows. */}
                <style>{SETTINGS_PAGE_HOST_CSS}</style>
                <div className="tc-main-area" style={{ flex: 1, minWidth: 0 }}>
                  <div id={SETTINGS_PAGE_HOST_ID} />
                  <div className="tc-main-view">
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div
                            style={{
                              minHeight: '50vh',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'var(--tc-background-primary, #000000)',
                              color: 'var(--tc-text-primary, #FFFFFF)',
                            }}
                          >
                            <div style={{ textAlign: 'center' }}>
                              <div
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  border: '3px solid var(--tc-border-light, #333333)',
                                  borderTop: '3px solid var(--tc-border-focus, #007AFF)',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  margin: '0 auto 16px auto',
                                }}
                              />
                              <div>Loading...</div>
                            </div>
                          </div>
                        }
                      >
                        {renderContent()}
                      </Suspense>
                    </ErrorBoundary>
                  </div>
                </div>
              </div>
            </MyWorkProvider>
          </SignOutProvider>
        </AccountSwitchProvider>
      </AccountProvider>
    );
  }

  // Not authenticated - check if user is trying to access an authenticated view
  const hasAuthenticatedView = !!params.get('view') || isResourceDeepLink(params);

  // Show login screen only if the visitor is explicitly trying to reach
  // authenticated content (a dashboard view or a resource deep link).
  // Otherwise — bare "/" or any unmatched public path — fall through to the
  // marketing HomePage. Previously `!publicPage` here routed the bare root to
  // login, so going to the website landed unauthenticated visitors on the
  // login screen instead of the home page.
  if (hasAuthenticatedView) {
    // Only save redirect for record links (work order, note, invoice batch) from email/text links
    // General navigation states like ?view=dashboard:location should NOT be saved
    // This prevents stale redirects from affecting future logins
    savePostLoginRedirect(window.location.search);

    return (
      <AccountProvider initialUser={user} initialPermissions={permissions} isAuthenticated={isAuthenticated} isAuthLoading={isLoading}>
        <Suspense fallback={publicPageFallback}>
          <LoginScreen
            isLoading={isLoading}
            error={error}
            onSubmit={handleLogin}
            onPasswordlessSuccess={handlePasswordlessSuccess}
            initialForceResetToken={pendingPasswordReset?.required ? pendingPasswordReset.token : undefined}
            onForceResetHandled={() => setPendingPasswordReset(null)}
            fromLink={recordLinkKind(window.location.search)}
          />
        </Suspense>
      </AccountProvider>
    );
  }

  // Public page requested - show home page
  return (
    <Suspense fallback={publicPageFallback}>
      <HomePage />
    </Suspense>
  );
}

/**
 * Site switch, decided once per page load BEFORE any product hook runs.
 *
 * On a developer host (developer.turn-connect.com, developer.dev.turn-connect.com)
 * or with `?site=developer` (local dev), render the partner developer site
 * instead of the product app: no product login, auth refresh or app chrome.
 * It is the same bundle and nginx image — the developer host's ingress just
 * points at the same frontend Service — so this is the only fork. Everything
 * else (including /partners/docs on the product hosts) goes through App.
 */
function AppRoot() {
  if (typeof window !== 'undefined' && isDeveloperSite(window.location.hostname, window.location.search)) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--tc-background-primary, black)' }} />}>
        <DeveloperSite />
      </Suspense>
    );
  }
  return <App />;
}

export default AppRoot;
